import React, { useEffect, useRef, useState } from "react";
import { BookOpen, Square } from "lucide-react";
import narration from "../data/narration.json";
import { sentenceTimings } from "../lib/regalVoice";

const TYPE_KEYS = ["mangalsutra", "chains", "rings", "earrings", "bangles", "sets", "pendants", "bracelets"];

export function storyKeyFor(p) {
  if (!p) return "default";
  const t = p.types || [];
  const c = p.categories || [];
  if (t.includes("mangalsutra")) return "mangalsutra";
  if (c.includes("wedding-bands")) return "wedding-bands";
  if (c.includes("bullion")) return "bullion";
  if (c.includes("diamond")) return "diamond";
  if (c.includes("silver")) return "silver";
  const k = TYPE_KEYS.find((x) => t.includes(x));
  return k || "default";
}

const POSE_BY_KEY = {
  diamond: "loupe",
  bullion: "goldbar",
  silver: "diya",
  mangalsutra: "necklace",
  sets: "necklace",
  "wedding-bands": "necklace",
  chains: "display",
  bracelets: "display",
  default: "welcome",
};

// "Hear the story of this piece" — Mr. Regal narrates; multiple tellings rotate per product & replay
export function ProductStory({ product }) {
  const key = storyKeyFor(product);
  const variants = Array.isArray(narration.categoryStories[key])
    ? narration.categoryStories[key]
    : [narration.categoryStories[key]];
  const pose = POSE_BY_KEY[key] || "gemstone";
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [variant, setVariant] = useState(() => {
    let h = 0;
    const s = product?.handle || "";
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return h % variants.length;
  });
  const text = variants[variant] || variants[0];
  const audioRef = useRef(null);
  const timers = useRef([]);

  const clear = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  const stop = () => {
    clear();
    if (audioRef.current) audioRef.current.pause();
    audioRef.current = null;
    setOpen(false);
    setIdx(0);
    setVariant((v) => (v + 1) % variants.length);
  };

  useEffect(() => () => {
    clear();
    if (audioRef.current) audioRef.current.pause();
  }, []);

  useEffect(() => {
    stop();
    let h = 0;
    const s = product?.handle || "";
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    setVariant(h % variants.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.handle]);

  const schedule = (total) => {
    clear();
    sentenceTimings(text, total).forEach((s, i) =>
      timers.current.push(setTimeout(() => setIdx(i), s.start))
    );
    timers.current.push(setTimeout(stop, total + 1800));
  };

  const start = () => {
    sessionStorage.setItem("rj_interacted", "1");
    setOpen(true);
    setIdx(0);
    schedule(Math.min(34000, Math.max(12000, text.length * 62)));
    try {
      const src = variant === 0 ? `/audio/story-cat-${key}.mp3` : `/audio/story-cat-${key}-${variant + 1}.mp3`;
      const a = new Audio(src);
      audioRef.current = a;
      a.volume = 0.6;
      a.onloadedmetadata = () => {
        if (a.duration && isFinite(a.duration)) schedule(a.duration * 1000);
      };
      a.onended = stop;
      a.play().catch(() => {});
    } catch (e) {
      console.debug("story audio failed:", e?.message);
    }
  };

  const segs = sentenceTimings(text, 1000);

  return (
    <div className="mt-4">
      {!open ? (
        <button
          onClick={start}
          data-testid="product-story-btn"
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase font-medium text-[#8a6a1f] border border-[#c9a24b]/60 px-4 py-2.5 hover:bg-[#c9a24b]/10 transition-colors"
        >
          <BookOpen size={13} /> Hear the Story of This Piece
        </button>
      ) : (
        <div className="border border-[#e0d5bb] bg-[#f4eddc] p-4 flex gap-4 items-start" data-testid="product-story-panel">
          <img src={`/mr-regal-${pose}.webp`} alt="" aria-hidden="true" loading="lazy" className="h-28 w-auto shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#a8842c]">Mr. Regal narrates</p>
            <p
              key={idx}
              className="font-heading italic text-sm sm:text-base leading-relaxed text-[#3f3a30] mt-2 min-h-[3.5rem]"
              data-testid="product-story-subtitle"
              aria-live="polite"
            >
              “{segs[idx]?.text || ""}”
            </p>
            <div className="flex items-center gap-3 mt-3">
              <button
                onClick={stop}
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-[#8a6a1f] hover:text-[#171310] transition-colors"
                data-testid="product-story-stop"
              >
                <Square size={11} /> Stop
              </button>
              <span className="text-[11px] text-[#8f8571]">{idx + 1} / {segs.length}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
